import { includes } from 'es-toolkit/compat';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { getCurrentUserPreferences, updateCurrentUserPreferences } from 'api/userPreferences';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { useAppSelector } from 'redux/hooks';
import { useCurrentLabId } from './useCurrentLab';
import { useEncodedFilters } from './useEncodedFilters';
import { shouldRestoreNavigationPreferencesOnRoute } from './navigationPreferences';
import {
  buildLastStudyPreferencesPatch,
  getLastStudyFromUserPreferences,
  getStoredLastStudyPreference,
  setStoredLastStudyPreference,
} from './userPreferences';

const PATHS_WITH_STUDY_PREFERENCE = ['/', '/procedures', '/uploads-dashboard'];

export const useSetInitialStudyId = () => {
  const location = useLocation();
  const { labId } = useCurrentLabId();
  const { queryParams, setQueryParams } = useEncodedFilters();
  const { accessToken, triedAuthenticateFromLocalStorage } = useAppSelector((state) => state.auth);

  const loadedAccessTokenRef = useRef<string | null>(null);
  const persistedStudyRef = useRef<string | null>(null);

  const currentStudyId = queryParams.filters?.studyId;
  const hasExplicitStudyFilter = currentStudyId !== undefined && currentStudyId !== null;
  const hasSpecificStudyFilter =
    Boolean(currentStudyId) && !includes(NON_SPECIFIC_STUDY_VALUES, String(currentStudyId));
  const canApplyRememberedStudy = includes(PATHS_WITH_STUDY_PREFERENCE, location.pathname) && !queryParams.cohortId;
  const canRestoreLoginStudyPreference = canApplyRememberedStudy && shouldRestoreNavigationPreferencesOnRoute(location);

  useEffect(() => {
    if (!canRestoreLoginStudyPreference || hasExplicitStudyFilter) {
      return;
    }

    const storedLastStudy = getStoredLastStudyPreference();
    if (!storedLastStudy) {
      return;
    }

    if (storedLastStudy?.labId !== labId) {
      return;
    }

    setQueryParams(
      {
        filters: { ...queryParams.filters, studyId: storedLastStudy.studyId },
        page: 1,
      },
      'replaceIn'
    );
  }, [canRestoreLoginStudyPreference, hasExplicitStudyFilter, labId, queryParams.filters, setQueryParams]);

  useEffect(() => {
    if (!triedAuthenticateFromLocalStorage || !canRestoreLoginStudyPreference) {
      return;
    }

    if (!accessToken) {
      loadedAccessTokenRef.current = null;
      persistedStudyRef.current = null;
      return;
    }

    if (loadedAccessTokenRef.current === accessToken) {
      return;
    }

    loadedAccessTokenRef.current = accessToken;

    let ignoreResponse = false;

    void getCurrentUserPreferences()
      .then((preferences) => {
        if (ignoreResponse) {
          return;
        }

        const serverLastStudy = getLastStudyFromUserPreferences(preferences ?? {});
        if (serverLastStudy) {
          setStoredLastStudyPreference(serverLastStudy);

          if (!hasExplicitStudyFilter && serverLastStudy.labId === labId) {
            setQueryParams(
              {
                filters: { ...queryParams.filters, studyId: serverLastStudy.studyId },
                page: 1,
              },
              'replaceIn'
            );
          }

          return;
        }

        const storedLastStudy = getStoredLastStudyPreference();
        if (storedLastStudy) {
          void updateCurrentUserPreferences(buildLastStudyPreferencesPatch(storedLastStudy)).catch(() => undefined);
        }
      })
      .catch(() => undefined);

    return () => {
      ignoreResponse = true;
    };
  }, [
    accessToken,
    canRestoreLoginStudyPreference,
    hasExplicitStudyFilter,
    labId,
    queryParams.filters,
    setQueryParams,
    triedAuthenticateFromLocalStorage,
  ]);

  useEffect(() => {
    if (!canApplyRememberedStudy || !hasSpecificStudyFilter || !labId) {
      return;
    }

    const nextLastStudy = {
      labId,
      studyId: String(currentStudyId),
    };

    setStoredLastStudyPreference(nextLastStudy);

    if (!accessToken) {
      return;
    }

    const serializedLastStudy = JSON.stringify(nextLastStudy);
    if (persistedStudyRef.current === serializedLastStudy) {
      return;
    }

    persistedStudyRef.current = serializedLastStudy;
    void updateCurrentUserPreferences(buildLastStudyPreferencesPatch(nextLastStudy)).catch(() => undefined);
  }, [accessToken, canApplyRememberedStudy, currentStudyId, hasSpecificStudyFilter, labId]);
};
